import { useState, useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Cookie, X } from 'lucide-react';

import { useLocalStorage } from '@/hooks/useLocalStorage';
import { setAnalyticsConsent } from '@/utils/analytics';

const DECISION_KEY = 'insidePanama_cookieConsent';
const MARKETING_KEY = 'cookie_consent_marketing';
const RECONSENT_AFTER_DAYS = 395;
const RECONSENT_AFTER_MS = RECONSENT_AFTER_DAYS * 24 * 60 * 60 * 1000;

/**
 * Cookie consent banner. Migrated teal → bordeaux (mai 2026).
 */
export default function CookieBanner() {
  const { t } = useTranslation('components');
  const [decision, setDecision] = useLocalStorage(DECISION_KEY, null);
  const [, setMarketing] = useLocalStorage(MARKETING_KEY, false);
  const [showSettings, setShowSettings] = useState(false);
  const [reopened, setReopened] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(true);
  const [marketingOn, setMarketingOn] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      if (decision) {
        setAnalyticsOn(Boolean(decision.analytics));
        setMarketingOn(Boolean(decision.marketing));
      }
      setReopened(true);
      setShowSettings(true);
    };
    window.addEventListener('inside-panama:open-cookie-prefs', handler);
    return () => window.removeEventListener('inside-panama:open-cookie-prefs', handler);
  }, [decision]);

  const persist = (decisionType, analytics, marketing) => {
    setDecision({ decision: decisionType, analytics, marketing, timestamp: Date.now() });
    setAnalyticsConsent(Boolean(analytics));
    setMarketing(Boolean(marketing));
    setReopened(false);
    setShowSettings(false);
  };

  const acceptAll = () => persist('accepted', true, true);
  const rejectAll = () => persist('declined', false, false);
  const saveCustom = () => persist('custom', analyticsOn, marketingOn);

  const isStale = (() => {
    if (!decision || !decision.timestamp) return false;
    return Date.now() - decision.timestamp > RECONSENT_AFTER_MS;
  })();

  const visible = !decision || isStale || reopened;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="region"
          aria-labelledby="cookie-banner-title"
          initial={reduce ? false : { y: 32, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={reduce ? { opacity: 0 } : { y: 32, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed inset-x-0 bottom-0 z-40 px-3 pb-3 sm:px-4 sm:pb-4 print-hide"
          data-print="hide"
        >
          <div className="mx-auto max-w-content overflow-hidden rounded-card border border-navy-900/10 bg-cream-50/95 shadow-card-hover backdrop-blur dark:border-white/10 dark:bg-zinc-950/95 dark:shadow-none">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 sm:flex-nowrap sm:px-5 sm:py-3.5">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bordeaux-600/10 text-bordeaux-700 dark:bg-bordeaux-400/20 dark:text-bordeaux-300">
                <Cookie size={16} aria-hidden="true" />
              </span>
              <p
                id="cookie-banner-title"
                className="flex-1 text-xs leading-relaxed text-gray-700 sm:text-sm dark:text-white/70"
              >
                <span className="font-semibold text-navy-900 dark:text-white">
                  {isStale
                    ? t('cookieBanner.reconsentTitle', { defaultValue: 'Vos préférences cookies ont 13 mois — pouvez-vous les confirmer ?' })
                    : t('cookieBanner.title')}
                </span>{' '}
                <span className="hidden sm:inline">— {t('cookieBanner.body')}</span>
              </p>
              <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                <button onClick={rejectAll} className="rounded-btn px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-cream-100 sm:text-sm dark:text-white/70 dark:hover:bg-white/5">
                  {t('cookieBanner.decline')}
                </button>
                <button
                  onClick={() => setShowSettings((s) => !s)}
                  aria-expanded={showSettings}
                  className="rounded-btn px-3 py-1.5 text-xs font-medium text-navy-900 hover:bg-cream-100 sm:text-sm dark:text-white dark:hover:bg-white/10"
                >
                  {t('cookieBanner.settings')}
                </button>
                <button
                  onClick={acceptAll}
                  className="rounded-btn bg-bordeaux-600 px-3 py-1.5 text-xs font-semibold text-cream-50 hover:bg-bordeaux-700 sm:text-sm"
                >
                  {t('cookieBanner.accept')}
                </button>
              </div>
            </div>

            <AnimatePresence initial={false}>
              {showSettings && (
                <motion.div
                  key="settings"
                  initial={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
                  animate={reduce ? { opacity: 1 } : { opacity: 1, height: 'auto' }}
                  exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="overflow-hidden border-t border-navy-900/10 px-4 py-4 sm:px-5 sm:py-5 dark:border-white/10"
                >
                  <ul className="space-y-3">
                    <Tier
                      title={t('cookieBanner.tier.functional')}
                      desc={t('cookieBanner.tier.functionalDesc')}
                      checked
                      disabled
                    />
                    <Tier
                      title={t('cookieBanner.tier.analytics')}
                      desc={t('cookieBanner.tier.analyticsDesc')}
                      checked={analyticsOn}
                      onChange={setAnalyticsOn}
                    />
                    <Tier
                      title={t('cookieBanner.tier.marketing')}
                      desc={t('cookieBanner.tier.marketingDesc')}
                      checked={marketingOn}
                      onChange={setMarketingOn}
                    />
                  </ul>
                  <div className="mt-4 flex justify-end gap-2">
                    <button onClick={() => setShowSettings(false)} className="btn-ghost text-sm">
                      <X size={14} aria-hidden="true" />
                      {t('common:close', { defaultValue: 'Fermer' })}
                    </button>
                    <button onClick={saveCustom} className="btn-primary text-sm">
                      {t('cookieBanner.saveSettings')}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Tier({ title, desc, checked, disabled = false, onChange }) {
  return (
    <li className="flex items-start justify-between gap-4">
      <div className="flex-1">
        <p className="text-sm font-semibold text-navy-900 dark:text-white">{title}</p>
        <p className="mt-0.5 text-xs text-gray-700 dark:text-white/60">{desc}</p>
      </div>
      <label
        className={`relative inline-flex shrink-0 cursor-pointer items-center ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
      >
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.checked)}
          // WCAG 4.1.2 (audit a11y 2026-07-08) : le <label> parent ne contient
          // que des spans aria-hidden (toggle visuel) → l'input n'avait AUCUN
          // nom accessible. Le titre du palier devient son étiquette SR.
          aria-label={title}
        />
        <span
          aria-hidden="true"
          className="h-6 w-10 rounded-full bg-gray-500/30 transition-colors peer-checked:bg-bordeaux-600 peer-focus-visible:ring-2 peer-focus-visible:ring-bordeaux-600 peer-focus-visible:ring-offset-2"
        />
        <span
          aria-hidden="true"
          className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-cream-50 shadow transition-transform peer-checked:translate-x-4"
        />
      </label>
    </li>
  );
}

export function openCookiePreferences() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('inside-panama:open-cookie-prefs'));
}
